import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { zFeatureFlagKey } from '../../featureFlags';
import { parseCents } from '../../utils/cents';
import { zOnlinePaymentGateway, zOnlinePaymentType } from '../enums';
import { type AreEqual, assertTypes, zPrice, zText } from '../primitives';
import { deliverySuburbCvxSchema } from './deliverySuburbSchema';
import { nativeAppVersionTypeSchema } from './nativeAppVersionTypeSchema';
import { serviceHourSchema } from './serviceHourSchema';

export const restaurantSchema = z.object({
  abn: z.string().nullable().default(null).describe('ABN // Australian Business Number'),
  activeFeatureFlags: z.array(zFeatureFlagKey).default([]).optional(),
  address: z.string().nullable().default(null),
  // autoAcceptEstWaitTime: z.number().optional(),
  /** Banner text shown to customers across consumer-web and consumer-native. */
  customerBannerText: z.string().nullable().default(null).optional(),
  consumerAppUrlAndroid: z
    .string()
    .nullable()
    .default(null)
    .describe('Consumer App URL for Android'),
  // RestaurantConfig contains semi-sensitive information, and we don't want to expose this to the client by mistake.
  consumerAppUrlIos: z.string().nullable().default(null).describe('Consumer App URL for iOS'), // Seems .url() doesn't work well with null values?
  deliveryEnabled: z.boolean().default(false),
  deliveryMinOrder: z.number().default(0),
  deliverySuburbs: z.array(deliverySuburbCvxSchema).default([]),
  legacyId: z.string().nullable().default(null),
  loyaltyCardEnabled: z.boolean().nullable().default(null),
  loyaltyCardTarget: z.number().nullable().default(null),
  modificationTime: z.number().nullable().default(null),
  name: z.string(),
  nativeAppVersion: nativeAppVersionTypeSchema.nullable().default(null),
  /**
   * Timestamp when an admin manually paused online orders, or `null` if not paused.
   * When set, `openStatus.isOnlineNow` is forced to `false` (immediately by the pause mutation,
   * and sustained by the cron job), causing `operationalStatus` to resolve to `'OFFLINE'`.
   * The admin UI distinguishes pause from device-offline by checking this field directly, not via `operationalStatus`.
   */
  onlineOrdersPausedAt: z.number().nullable().default(null).optional(),
  onlinePaymentGateway: zOnlinePaymentGateway.nullable().default(null).optional(),
  onlinePaymentCashLimit: z.number().nullable().default(null),
  onlinePaymentTypes: z.array(zOnlinePaymentType).default([]),
  phone: z.string().nullable().default(null),
  postcode: z.string().nullable().default(null),
  serviceHours: z.array(serviceHourSchema).default([]),
  /** The restaurant's Stripe account id, which is a Stripe Connect account (a sub-account of the platform Stripe account). https://docs.stripe.com/connect  */
  stripeConnectAccountId: z.string().nullable().default(null),
  // Remove timeZone default later on...
  // Don't store the offset, like -600, because this can change due to day light savings.
  timeZone: z.string().default('Australia/Melbourne'),
  /** `uuid` is needed in addition to Convex `_id`, so that we can have a stable id reference that can be manually define.
   * `_id` is generated automatically by Convex and we are unable to set this to a specific value.
   *  This `uuid` value is hardcoded in native apps, and is also hardcoded in RestaurantCustomer Cognito profile username.
   *  `_id` should only be used for linked documents in Convex (not `uuid`).
   **/
  uuid: z.string().default(() => uuidv4()), // FIXME: Should be uuid() but has some records with invalid uuids at the moment
  website: z.string().nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type Restaurant = z.output<typeof restaurantSchema>;

export const restaurantStrictSchema = restaurantSchema
  .safeExtend({
    deliveryMinOrder: zPrice,
    loyaltyCardTarget: z.number().min(2).max(9).nullable(),
    name: zText,
    onlinePaymentCashLimit: zPrice.nullable(),
    website: z.url().nullable(),
  })
  /** Validate: If Stripe is the gateway and credit cards are accepted, then Stripe account ID must be set */
  .refine(
    (restaurant) => {
      if (
        restaurant.onlinePaymentTypes.includes('CREDIT_CARD') &&
        restaurant.onlinePaymentGateway === 'STRIPE'
      ) {
        return !!restaurant.stripeConnectAccountId;
      }
      return true;
    },
    {
      message: 'If credit cards are accepted with Stripe, then Stripe account ID must be set',
      path: ['stripeConnectAccountId', 'onlinePaymentTypes'],
    },
  );
/** The strict schema should not change the output types */
assertTypes<AreEqual<Restaurant, z.infer<typeof restaurantStrictSchema>>>();

// When reading values from the Convex (cvx) database.
// Note, base on restaurantSchema and not restaurantValidSchema because "valid" values are stores in database
// in a best effort way. If restaurantValid schema changes and makes a stored restaurant invalid, it should
// not throw (thereby breaking the frontend app).
export const restaurantCvxSchema = restaurantSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type RestaurantCvx = z.output<typeof restaurantCvxSchema>;

export const restaurantPublicCvxSchema = restaurantCvxSchema.pick({
  _id: true,
  abn: true,
  address: true,
  customerBannerText: true,
  deliveryEnabled: true,
  deliveryMinOrder: true,
  deliverySuburbs: true,
  loyaltyCardEnabled: true,
  loyaltyCardTarget: true,
  name: true,
  onlinePaymentCashLimit: true,
  onlinePaymentGateway: true,
  onlinePaymentTypes: true,
  phone: true,
  postcode: true,
  serviceHours: true,
  stripeConnectAccountId: true,
  uuid: true,
});
export type RestaurantPublicCvx = z.output<typeof restaurantPublicCvxSchema>;

//
// Forms
//

export const restaurantForms = {
  generalSettings: z.object(restaurantStrictSchema.shape).pick({
    abn: true,
    address: true,
    customerBannerText: true,
    name: true,
    phone: true,
    postcode: true,
    timeZone: true,
    website: true,
  }),

  platformSettings: restaurantCvxSchema
    .pick({
      onlinePaymentCashLimit: true,
      onlinePaymentGateway: true,
      onlinePaymentTypes: true,
      stripeConnectAccountId: true,
      uuid: true,
    })
    .extend({
      onlinePaymentCashLimit: z.preprocess(parseCents, zPrice.nullable().default(null)),
    }),

  featureFlags: restaurantCvxSchema.pick({ activeFeatureFlags: true }),

  deliverySettings: restaurantSchema
    .pick({
      deliveryEnabled: true,
      deliveryMinOrder: true,
      deliverySuburbs: true,
    })
    .extend({
      deliveryMinOrder: z.preprocess(parseCents, zPrice.default(0)),
    }),

  loyaltyCard: restaurantCvxSchema
    .pick({
      loyaltyCardEnabled: true,
      loyaltyCardTarget: true,
    })
    .extend({
      categoryUuids: z.array(z.string()),
    }),
};
